import React from 'react';
import {
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Github,
  XCircle,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  getAvatarInitials,
  getAvatarPaletteClassName,
  groupWebhookLogsByDay,
} from './webHookTimelineUtils';

const getStatusBadgeClassName = (statusKey) => {
  if (statusKey === 'success') {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200';
  }

  if (statusKey === 'failed') {
    return 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-200';
  }

  return 'bg-muted text-foreground dark:bg-muted/50';
};

const getAccentClassName = (statusKey) => {
  if (statusKey === 'success') {
    return 'bg-emerald-500';
  }

  if (statusKey === 'failed') {
    return 'bg-rose-500';
  }

  return 'bg-border';
};

const WebHookTimelineFeed = ({
  logs,
  onOpenDetails,
}) => {
  const groups = React.useMemo(() => groupWebhookLogsByDay(logs), [logs]);

  return (
    <div className="space-y-8">
      {groups.map((group) => (
        <section key={group.dayKey} className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {group.label}
            </span>
            <div className="h-px flex-1 bg-border/35" />
            <span className="text-[12px] text-muted-foreground">
              {group.failedCount > 0 ? (
                <>
                  {group.totalCount} deliveries -{' '}
                  <span className="text-emerald-700 dark:text-emerald-300">
                    {group.successCount} ok
                  </span>{' '}
                  -{' '}
                  <span className="text-rose-700 dark:text-rose-300">
                    {group.failedCount} failed
                  </span>
                </>
              ) : (
                <>
                  {group.totalCount} deliveries -{' '}
                  <span className="text-emerald-700 dark:text-emerald-300">
                    all ok
                  </span>
                </>
              )}
            </span>
          </div>

          <div className="space-y-3">
            {group.items.map((log) => (
              <article key={log.key} className="relative pl-[3px]">
                <div
                  className={cn(
                    'absolute inset-y-0 left-0 w-[3px]',
                    getAccentClassName(log.statusKey)
                  )}
                />

                <button
                  type="button"
                  className="flex w-full appearance-none items-center gap-4 rounded-r-2xl rounded-l-none border border-border/35 bg-background/95 px-4 py-4 text-left shadow-none transition-colors hover:bg-primary/3 dark:bg-card/25"
                  onClick={() => onOpenDetails(log)}
                >
                  <div
                    className={cn(
                      'flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold',
                      getAvatarPaletteClassName(log.authorLabel)
                    )}
                  >
                    {getAvatarInitials(log.authorLabel)}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="mb-1.5 flex flex-wrap items-center gap-2">
                      <span className="text-[18px] font-semibold text-foreground">
                        {log.authorLabel}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[12px] font-mono text-muted-foreground">
                        <Github className="h-3.5 w-3.5" />
                        {log.repoLabel}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-border/35 bg-muted/70 px-2.5 py-1 text-[11px] font-medium text-muted-foreground dark:bg-background/45">
                        {log.eventLabel}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] font-mono text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock3 className="h-3.5 w-3.5" />
                        {log.createdAtLabel}
                      </span>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-3">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold capitalize',
                        getStatusBadgeClassName(log.statusKey)
                      )}
                    >
                      {log.statusKey === 'success' ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : log.statusKey === 'failed' ? (
                        <XCircle className="h-3.5 w-3.5" />
                      ) : (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      )}
                      {log.statusLabel}
                    </span>

                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </button>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default WebHookTimelineFeed;
